import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Property } from '../../../shared/types/Property';
import { BuildingIcon, TagIcon, MapPinIcon, PhoneIcon, GlobeIcon, ClockIcon, CheckIcon, XIcon, InfoIcon } from 'lucide-react';
import { InfoTooltip } from '../../../shared/ui/InfoTooltip';
export function PropertyInformation() {
  const {
    t
  } = useTranslation('rooms');
  // Mock data for a Malaysian resort
  const [property, setProperty] = useState<Property>({
    name: 'Langkawi Bay Tropical Paradise Resort',
    description: 'Nestled on the pristine shores of Langkawi, Langkawi Bay Tropical Paradise Resort offers a perfect blend of Malaysian luxury and natural beauty. Surrounded by lush tropical rainforest and overlooking the crystal-clear waters of the Andaman Sea, this exclusive retreat provides an authentic Malaysian experience with modern comforts.',
    designation: 'Resort',
    starRating: 5,
    tags: ['Beach Access', 'Spa Retreat', 'Family-Friendly', 'Luxury', 'Malaysian Cuisine', 'Beachfront', 'Island Escape', 'Wellness-Retreat', 'All-Inclusive', 'Yoga Classes', 'Eco-Friendly', 'Cultural Experiences'],
    address: {
      office: '124 Jalan Pantai Cenang',
      city: 'Langkawi',
      postalCode: '07000',
      country: 'Malaysia',
      latitude: '6.2885',
      longitude: '99.7325'
    },
    contact: {
      email: 'reservations@langkawibaytropical.com',
      phone: '+60 4 955 1234',
      website: 'https://www.langkawibaytropicalresort.com',
      timezone: 'Asia/Kuala_Lumpur'
    },
    checkIn: '14:00',
    checkOut: '12:00',
    amenities: ['Infinity Pool', 'Private Pool Villas', 'WiFi', 'Airport Shuttle', 'Daily Housekeeping', 'Smart TV', 'Hair Dryer', 'In-Room Safe', 'Bicycle Rental', 'Malaysian Cooking Classes', 'Sunset Cruise', 'Island Hopping', 'Floating Breakfast', 'Traditional Malay Massage', 'Silat Classes', 'Mangrove Boat Tours'],
    transportation: ['Taxi', 'Hotel Shuttle', 'Motorbike Rental', 'Longtail Boat', 'Ferry', 'Domestic Flight', 'Private Van Transfer']
  });
  // Function to render tags
  const renderTags = (tags: string[]) => {
    return <div className="flex flex-wrap gap-2" data-id="element-8935">
        {tags.map((tag, index) => <div key={index} className="flex items-center bg-gray-100 text-gray-700 text-xs px-2 py-1 rounded" data-id="element-8936">
            {tag}
            <button aria-label={`Remove ${tag}`} className="ml-1 text-gray-500 hover:text-gray-700" data-id="element-8937">
              <XIcon size={12} aria-hidden="true" data-id="element-8938" />
            </button>
          </div>)}
      </div>;
  };
  // Draft information tooltip content
  const draftInfoContent = "Your hotel listing is currently in draft mode. It won't be visible until your booking engine is enabled. Once you add availability or offers through your inventory management, our team will review and publish them when ready.";
  return <div className="space-y-6" data-id="element-8939">
      {/* Simplified header section */}
      <div data-id="element-8940">
        <h1 className="text-2xl font-semibold text-gray-800 mb-4" data-id="element-8941">
          {t('pages.PropertyInformation.hotel')}
        </h1>
        <div className="flex justify-between items-center" data-id="element-8942">
          {/* Left side - tabs */}
          <div className="border-b border-gray-200" data-id="element-8943">
            <div className="flex space-x-6" data-id="element-8944">
              <button className="text-sm font-medium text-blue-600 border-b-2 border-blue-600 pb-2" data-id="element-8945">
                {t('pages.PropertyInformation.hotel_information')}
              </button>
              <button className="text-sm font-medium text-gray-500 hover:text-gray-800 pb-2" data-id="element-8946">
                {t('components.EditRoomForm.media_assets')}
              </button>
            </div>
          </div>
          {/* Right side - status button with tooltip */}
          <div className="relative group" data-id="element-8947">
            <button className="flex items-center bg-gray-200 text-gray-700 px-4 py-2 rounded-md text-sm font-medium" data-id="element-8948">
              <span className="mr-2" data-id="element-8949">
                {t('pages.PropertyInformation.draft_submitted_for_review')}
              </span>
              <InfoIcon size={16} className="text-gray-500" data-id="element-8950" />
            </button>
            <div className="absolute right-0 mt-2 w-72 p-3 bg-white border border-gray-200 rounded-lg shadow-lg z-10 hidden group-hover:block" data-id="element-8951">
              <h3 className="text-sm font-medium text-gray-800 mb-1" data-id="element-8952">
                {t('pages.PropertyInformation.draft_status')}
              </h3>
              <p className="text-xs text-gray-600" data-id="element-8953">{draftInfoContent}</p>
            </div>
          </div>
        </div>
      </div>
      {/* Hotel Details Form */}
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-6" data-id="element-8954">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6" data-id="element-8955">
          <div data-id="element-8956">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-8957">
              {t('pages.PropertyInformation.hotel_name')}
            </label>
            <input type="text" className="w-full border border-gray-300 rounded-md px-3 py-2" value={property.name} onChange={e => setProperty({
            ...property,
            name: e.target.value
          })} data-id="element-8958" />
          </div>
          <div data-id="element-8959">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-8960">
              {t('pages.PropertyInformation.designation')}
            </label>
            <div className="relative" data-id="element-8961">
              <select className="w-full border border-gray-300 rounded-md px-3 py-2 appearance-none" value={property.designation} onChange={e => setProperty({
              ...property,
              designation: e.target.value
            })} data-id="element-8962">
                <option value="Resort" data-id="element-8963">
                  {t('pages.PropertyInformation.resort')}
                </option>
                <option value="Hotel" data-id="element-8964">
                  {t('pages.PropertyInformation.hotel')}
                </option>
                <option value="Villa" data-id="element-8965">
                  {t('pages.PropertyInformation.villa')}
                </option>
                <option value="Guesthouse" data-id="element-8966">
                  {t('pages.PropertyInformation.guesthouse')}
                </option>
              </select>
              <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none" data-id="element-8967">
                <ChevronDownIcon size={16} className="text-gray-400" data-id="element-8968" />
              </div>
            </div>
          </div>
        </div>
        <div className="mb-6" data-id="element-8969">
          <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-8970">
            {t('components.AddRoomForm.description')}
          </label>
          <textarea className="w-full border border-gray-300 rounded-md px-3 py-2 h-24" value={property.description} onChange={e => setProperty({
          ...property,
          description: e.target.value
        })} data-id="element-8971" />
        </div>
        <div className="mb-6" data-id="element-8972">
          <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-8973">
            {t('pages.PropertyInformation.star_rating')}
          </label>
          <div className="relative" data-id="element-8974">
            <select className="w-full border border-gray-300 rounded-md px-3 py-2 appearance-none" value={property.starRating} onChange={e => setProperty({
            ...property,
            starRating: parseInt(e.target.value)
          })} data-id="element-8975">
              <option value="5" data-id="element-8976">5</option>
              <option value="4" data-id="element-8977">4</option>
              <option value="3" data-id="element-8978">3</option>
              <option value="2" data-id="element-8979">2</option>
              <option value="1" data-id="element-8980">1</option>
            </select>
            <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none" data-id="element-8981">
              <ChevronDownIcon size={16} className="text-gray-400" data-id="element-8982" />
            </div>
          </div>
        </div>
        <div className="mb-6" data-id="element-8983">
          <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-8984">
            {t('pages.PropertyInformation.tags')}
          </label>
          {renderTags(property.tags)}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-8985">
          <div data-id="element-8986">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-8987">
              {t('pages.PropertyInformation.address')}
            </label>
            <input type="text" className="w-full border border-gray-300 rounded-md px-3 py-2 mb-3" value={property.address.office} onChange={e => setProperty({
            ...property,
            address: {
              ...property.address,
              office: e.target.value
            }
          })} data-id="element-8988" />
          </div>
          <div data-id="element-8989">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-8990">
              {t('pages.PropertyInformation.area_name')}
            </label>
            <input type="text" className="w-full border border-gray-300 rounded-md px-3 py-2 mb-3" value="Pantai Cenang" data-id="element-8991" />
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-8992">
          <div data-id="element-8993">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-8994">
              {t('pages.PropertyInformation.city')}
            </label>
            <input type="text" className="w-full border border-gray-300 rounded-md px-3 py-2 mb-3" value={property.address.city} onChange={e => setProperty({
            ...property,
            address: {
              ...property.address,
              city: e.target.value
            }
          })} data-id="element-8995" />
          </div>
          <div data-id="element-8996">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-8997">
              {t('pages.PropertyInformation.postal_code')}
            </label>
            <input type="text" className="w-full border border-gray-300 rounded-md px-3 py-2 mb-3" value={property.address.postalCode} onChange={e => setProperty({
            ...property,
            address: {
              ...property.address,
              postalCode: e.target.value
            }
          })} data-id="element-8998" />
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6" data-id="element-8999">
          <div data-id="element-9000">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-9001">
              {t('pages.PropertyInformation.country')}
            </label>
            <div className="relative" data-id="element-9002">
              <select className="w-full border border-gray-300 rounded-md px-3 py-2 appearance-none" value={property.address.country} onChange={e => setProperty({
              ...property,
              address: {
                ...property.address,
                country: e.target.value
              }
            })} data-id="element-9003">
                <option value="Malaysia" data-id="element-9004">
                  {t('pages.PropertyInformation.malaysia')}
                </option>
                <option value="Indonesia" data-id="element-9005">
                  {t('pages.PropertyInformation.indonesia')}
                </option>
              </select>
              <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none" data-id="element-9006">
                <ChevronDownIcon size={16} className="text-gray-400" data-id="element-9007" />
              </div>
            </div>
          </div>
          <div data-id="element-9008">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-9009">
              {t('pages.PropertyInformation.latitude')}
            </label>
            <input type="text" className="w-full border border-gray-300 rounded-md px-3 py-2" value={property.address.latitude} onChange={e => setProperty({
            ...property,
            address: {
              ...property.address,
              latitude: e.target.value
            }
          })} data-id="element-9010" />
          </div>
          <div data-id="element-9011">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-9012">
              {t('pages.PropertyInformation.longitude')}
            </label>
            <input type="text" className="w-full border border-gray-300 rounded-md px-3 py-2" value={property.address.longitude} onChange={e => setProperty({
            ...property,
            address: {
              ...property.address,
              longitude: e.target.value
            }
          })} data-id="element-9013" />
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-9014">
          <div data-id="element-9015">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-9016">
              {t('pages.PropertyInformation.contact_email')}
            </label>
            <input type="email" className="w-full border border-gray-300 rounded-md px-3 py-2 mb-3" value={property.contact.email} onChange={e => setProperty({
            ...property,
            contact: {
              ...property.contact,
              email: e.target.value
            }
          })} data-id="element-9017" />
          </div>
          <div data-id="element-9018">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-9019">
              {t('pages.PropertyInformation.phone_number')}
            </label>
            <input type="text" className="w-full border border-gray-300 rounded-md px-3 py-2 mb-3" value={property.contact.phone} onChange={e => setProperty({
            ...property,
            contact: {
              ...property.contact,
              phone: e.target.value
            }
          })} data-id="element-9020" />
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6" data-id="element-9021">
          <div data-id="element-9022">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-9023">
              {t('pages.PropertyInformation.website')}
            </label>
            <input type="text" className="w-full border border-gray-300 rounded-md px-3 py-2" value={property.contact.website} onChange={e => setProperty({
            ...property,
            contact: {
              ...property.contact,
              website: e.target.value
            }
          })} data-id="element-9024" />
          </div>
          <div data-id="element-9025">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-9026">
              {t('pages.PropertyInformation.timezone')}
            </label>
            <div className="relative" data-id="element-9027">
              <select className="w-full border border-gray-300 rounded-md px-3 py-2 appearance-none" value={property.contact.timezone} onChange={e => setProperty({
              ...property,
              contact: {
                ...property.contact,
                timezone: e.target.value
              }
            })} data-id="element-9028">
                <option value="Asia/Kuala_Lumpur" data-id="element-9029">
                  {t('pages.PropertyInformation.asiakualalumpur')}
                </option>
                <option value="Asia/Singapore" data-id="element-9030">
                  {t('pages.PropertyInformation.asiasingapore')}
                </option>
              </select>
              <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none" data-id="element-9031">
                <ChevronDownIcon size={16} className="text-gray-400" data-id="element-9032" />
              </div>
            </div>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-9033">
          <div data-id="element-9034">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-9035">
              {t('pages.PropertyInformation.checkin_time')}
            </label>
            <input type="time" className="w-full border border-gray-300 rounded-md px-3 py-2" value={property.checkIn} onChange={e => setProperty({
            ...property,
            checkIn: e.target.value
          })} data-id="element-9036" />
          </div>
          <div data-id="element-9037">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-9038">
              {t('pages.PropertyInformation.checkout_time')}
            </label>
            <input type="time" className="w-full border border-gray-300 rounded-md px-3 py-2" value={property.checkOut} onChange={e => setProperty({
            ...property,
            checkOut: e.target.value
          })} data-id="element-9039" />
          </div>
        </div>
        <div className="mb-6" data-id="element-9040">
          <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-9041">
            {t('pages.PropertyInformation.hotel_amenities')}
          </label>
          <div className="border border-gray-200 rounded-lg p-4" data-id="element-9042">
            <div className="flex flex-wrap gap-2" data-id="element-9043">
              {property.amenities.map((amenity, index) => <div key={index} className="flex items-center bg-gray-100 text-gray-700 text-xs px-2 py-1 rounded" data-id="element-9044">
                  {amenity}
                  <button aria-label={`Remove ${amenity}`} className="ml-1 text-gray-500 hover:text-gray-700" data-id="element-9045">
                    <XIcon size={12} aria-hidden="true" data-id="element-9046" />
                  </button>
                </div>)}
            </div>
          </div>
        </div>
        <div data-id="element-9047">
          <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-9048">
            {t('pages.PropertyInformation.transportation')}
          </label>
          <div className="space-y-2" data-id="element-9049">
            {property.transportation.map((item, index) => <div key={index} className="flex items-center" data-id="element-9050">
                <input type="checkbox" id={`transportation-${index}`} checked={true} className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500" data-id="element-9051" />

                <label htmlFor={`transportation-${index}`} className="ml-2 text-sm text-gray-700" data-id="element-9052">
                  {item}
                </label>
              </div>)}
          </div>
        </div>
      </div>
    </div>;
}
// Helper component for the ChevronDown icon
function ChevronDownIcon({
  size = 24,
  className = ''
}) {
  return <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} data-id="element-9053">
      <polyline points="6 9 12 15 18 9" data-id="element-9054"></polyline>
    </svg>;
}